import { extend, isNullOrUndefined, Animation } from '@syncfusion/ej2-base';
import { ChartLocation } from '../../common/utils/helper';
import { stopTimer, removeElement } from '../../common/utils/helper';
import { ChartData } from '../../chart/utils/get-data';
import { Tooltip as SVGTooltip } from '@syncfusion/ej2-svg-base';
import { indexFinder } from '../../common/utils/helper';
import { Selection } from '../../chart/user-interaction/selection';
/**
 * `Tooltip` module is used to render the tooltip for series.
 */
export class BaseTooltip extends ChartData {
    /**
     * Constructor for tooltip module.
     *
     * @private
     */
    constructor(chart) {
        super(chart);
        this.element = this.chart.element;
        this.textStyle = chart.tooltip.textStyle;
        this.control = chart;
        this.template = chart.tooltip.template;
    }
    getElement(id) {
        return document.getElementById(id);
    }
    /**
     * Renders the tooltip.
     *
     * @returns {void}
     * @private
     */
    getTooltipElement(isTooltip) {
        this.inverted = this.chart.requireInvertedAxis;
        this.header = (this.control.tooltip.header === null) ?
            ((this.control.tooltip.shared) ? '${point.x}' : '${series.name}')
            : (this.control.tooltip.header);
        this.formattedText = [];
        const tooltipDiv = document.getElementById(this.chart.element.id + '_tooltip');
        const isStockChart = this.chart.element.id.indexOf('stockChart') > -1;
        if (!isTooltip && !tooltipDiv || isStockChart) {
            return this.createElement();
        }
        return null;
    }
    createElement() {
        const tooltipDiv = document.createElement('div');
        tooltipDiv.id = this.element.id + '_tooltip';
        tooltipDiv.className = 'ejSVGTooltip';
        tooltipDiv.style.pointerEvents = 'none';
        tooltipDiv.style.position = 'absolute';
        tooltipDiv.style.zIndex = '1';
        return tooltipDiv;
    }
    pushData(data, isFirst, tooltipDiv, isChart, enable3D) {
        if (data.series.enableTooltip) {
            if (enable3D) {
                this.currentPoints.push(data);
            }
            else if (isChart) {
                this.currentPoints.push(data);
            }
            else {
                this.currentPoints.push(data);
            }
            this.stopAnimation();
            if (tooltipDiv && !document.getElementById(tooltipDiv.id)) {
                if (!this.chart.stockChart) {
                    document.getElementById(this.element.id + '_Secondary_Element').appendChild(tooltipDiv);
                }
                else {
                    document.getElementById(this.chart.stockChart.element.id + '_Secondary_Element').appendChild(tooltipDiv);
                }
            }
            return true;
        }
        return false;
    }
    removeHighlight() {
        let item;
        // let series: Series;
        for (let i = 0, len = this.previousPoints.length; i < len; i++) {
            item = this.previousPoints[i];
            if (item.series.isRectSeries || this.chart.tooltip.enableHighlight) {
                if (item.series.visible) {
                    this.highlightPoint(item.series, item.point.index, false);
                }
                continue;
            }
            // series = item.series as Series;
        }
    }
    /**
     * Animates the opacity change of the given element to simulate a highlight effect.
     *
     * @param {number} targetOpacity - The final opacity value to which the element's opacity will be animated.
     * @param {HTMLElement} targetElement - The DOM element whose opacity is to be animated.
     * @param {number} duration - The duration of the animation effect.
     * @param {number} targetStrokeWidth - The final stroke-width value to which the element's stroke-width will be animated.
     * @returns {void}
     * @private
     */
    animateHighlight(targetOpacity, targetElement, duration, targetStrokeWidth) {
        const initialOpacity = parseFloat(targetElement.getAttribute('opacity'));
        let initialStrokeWidth = null;
        if (targetStrokeWidth !== null) {
            initialStrokeWidth = parseFloat(targetElement.getAttribute('stroke-width'));
        }
        new Animation({}).animate(targetElement, {
            duration: duration,
            progress: (args) => {
                targetElement.style.animation = '';
                if (this.svgTooltip) {
                    return;
                }
                if (targetStrokeWidth !== null) {
                    const newStrokeWidth = initialStrokeWidth
                        + (args.timeStamp / args.duration) * (targetStrokeWidth - initialStrokeWidth);
                    targetElement.setAttribute('stroke-width', Math.max(newStrokeWidth, targetStrokeWidth).toString());
                }
                const newOpacity = initialOpacity + (args.timeStamp / args.duration) * (targetOpacity - initialOpacity);
                targetElement.setAttribute('opacity', Math.min(newOpacity, targetOpacity).toString());
            },
            end: () => {
                if (this.svgTooltip) {
                    return;
                }
                if (targetStrokeWidth !== null) {
                    targetElement.setAttribute('stroke-width', targetStrokeWidth.toString());
                }
                targetElement.setAttribute('opacity', targetOpacity.toString());
            }
        });
    }
    highlightPoint(series, pointIndex, highlight) {
        const element = series.type === 'BoxAndWhisker' ? this.getElement(this.element.id + '_Series_' + series.index + '_Point_' + pointIndex + '_BoxPath') : this.getElement(this.element.id + '_Series_' + series.index + '_Point_' + pointIndex);
        const selectionModule = this.control.accumulationSelectionModule;
        const isAccumulation = this.chart.getModuleName() === 'accumulationchart';
        const isSelectedElement = selectionModule && selectionModule.selectedDataIndexes.length > 0 ? true : false;
        if ((element) || (!series.isRectSeries)) {
            if ((!isSelectedElement || isSelectedElement && element.getAttribute('class')
                && element.getAttribute('class').indexOf('_ej2_chart_selection_series_') === -1) || (!series.isRectSeries)) {
                if (series.isRectSeries && this.chart.highlightColor !== '' && !isNullOrUndefined(this.chart.highlightColor)) {
                    element.setAttribute('fill', (highlight && this.chart.highlightColor !== 'transparent' ? this.chart.highlightColor : series.pointColorMapping !== '' ? (series.points[0]).color : series.points[pointIndex].color || series.interior));
                }
                else {
                    if ((this.control.highlightMode === 'None') && (this.chart.tooltip.enableHighlight) && ((!(this.chart.tooltip.shared || this.chart.tooltip.split)) || (isAccumulation))) {
                        if (highlight && (isAccumulation ? this.control.accumulationSelectionModule
                            && this.control.accumulationSelectionModule.selectedDataIndexes.length > 0
                            : this.chart.selectionModule && this.chart.selectionModule.selectedDataIndexes.length > 0)) {
                            return;
                        }
                        const target = this.element.id + '_Series_' + series.index + '_Point_' + pointIndex;
                        let selectionHelper = null;
                        for (const currentSeries of this.chart.visibleSeries) {
                            let seriesElementsGroupCollections = [];
                            const currentSeriesWidth = typeof currentSeries.width === 'number' ? currentSeries.width : parseFloat(currentSeries.width);
                            seriesElementsGroupCollections = isAccumulation
                                ? [this.getElement(this.chart.element.id + '_Series_' + currentSeries.index)]
                                : ((selectionHelper || (selectionHelper = new Selection(this.chart))).getSeriesElements(currentSeries));
                            if (isAccumulation && this.control.series[0].dataLabel.visible) {
                                const dataLabelCollection = this.getElement(this.element.id + '_datalabel_Series_0');
                                if (dataLabelCollection) {
                                    seriesElementsGroupCollections.push(dataLabelCollection);
                                }
                            }
                            const isTargetSeries = !isAccumulation && (currentSeries.index === series.index);
                            seriesElementsGroupCollections.forEach((seriesElementsGroup) => {
                                if (seriesElementsGroup !== null) {
                                    seriesElementsGroup.childNodes.forEach((seriesElement) => {
                                        const targetOpacity = seriesElement.id.indexOf('border') > -1 ? 1 :
                                            seriesElement.id.indexOf('Symbol') > -1 ? currentSeries.marker.opacity : currentSeries.opacity;
                                        const targetStrokeWidth = seriesElement.id.indexOf('border') > -1 && currentSeries.border.width
                                            ? parseFloat(currentSeries.border.width.toString())
                                            : seriesElement.id.indexOf('Symbol') > -1 && currentSeries.marker.border.width
                                                ? parseFloat(currentSeries.marker.border.width.toString())
                                                : currentSeriesWidth;
                                        if (highlight && this.chart.highlightColor !== 'transparent' && seriesElement.id !== '') {
                                            if (isAccumulation ? (seriesElementsGroup.getAttribute('id').indexOf('datalabel') > -1 ? indexFinder(seriesElement.id).point === pointIndex :
                                                seriesElement.id === target) : (seriesElementsGroup.getAttribute('id') === this.element.id + 'DataLabelCollection' ? (indexFinder(seriesElement.id).series === series.index) : (currentSeries.index === series.index))) {
                                                seriesElement.setAttribute('opacity', targetOpacity.toString());
                                                if ((!series.isRectSeries || (seriesElement.id.indexOf('border') > -1)) && (!isAccumulation)) {
                                                    seriesElement.setAttribute('stroke-width', (targetStrokeWidth + 1).toString());
                                                }
                                            }
                                            else {
                                                seriesElement.setAttribute('opacity', isAccumulation ? seriesElement.id.indexOf('datalabel') > -1 ? '0.5' : '0.3'
                                                    : (seriesElement.getAttribute('id').indexOf('Text') > -1 ? '0.5' : '0.3'));
                                                if ((!series.isRectSeries || (seriesElement.id.indexOf('border') > -1)) && (!isAccumulation)) {
                                                    seriesElement.setAttribute('stroke-width', (targetStrokeWidth).toString());
                                                }
                                            }
                                        }
                                        else if (!this.currentPoints[0] && seriesElement.id !== '') {
                                            this.animateHighlight(targetOpacity, seriesElement, this.chart.tooltip.duration, ((!series.isRectSeries || (seriesElement.id.indexOf('border') > -1)) && (!isAccumulation)) ? targetStrokeWidth : null);
                                        }
                                    });
                                }
                            });
                            if (!isAccumulation && selectionHelper && (!this.chart.highlightModule ||
                                this.chart.highlightModule.highlightDataIndexes.length === 0)) {
                                const labelSeriesIndex = selectionHelper.rangeColorMappingEnabled() ? 0 : currentSeries.index;
                                if (highlight) {
                                    selectionHelper.updateSeriesLabelOpacity(labelSeriesIndex, !isTargetSeries);
                                }
                                else {
                                    selectionHelper.updateSeriesLabelOpacity(labelSeriesIndex, false);
                                }
                            }
                        }
                    }
                    else if (series.isRectSeries) {
                        element.setAttribute('opacity', ((highlight && this.chart.highlightColor !== 'transparent' && this.chart.highlightMode !== 'Point') ? (series.opacity / 2) : series.opacity).toString());
                    }
                }
            }
            else {
                element.setAttribute('opacity', series.opacity.toString());
            }
        }
    }
    highlightPoints() {
        for (const item of this.currentPoints) {
            if ((item.series.isRectSeries || this.chart.tooltip.enableHighlight) && item.series.category === 'Series') {
                this.highlightPoint(item.series, item.point.index, true);
            }
        }
    }
    createTooltip(chart, isFirst, location, clipLocation, point, shapes, offset, bounds, crosshairEnabled = false, extraPoints = null, templatePoint = null, customTemplate, splitLocations, splitClipBounds, seriesTypes) {
        const series = this.currentPoints[0].series;
        const tooltipModule = chart.tooltipModule || chart.tooltip3DModule ||
            chart.accumulationTooltipModule;
        if (!isNullOrUndefined(point) && !isNullOrUndefined(series.gradientId)) {
            const hasGradientStops = (series.linearGradient.gradientColorStop.length) > 0 ||
                (series.radialGradient.gradientColorStop.length) > 0;
            if (hasGradientStops && !isNullOrUndefined(series.interior)) {
                point.color = series.interior;
            }
        }
        // For the tooltip enable is false.
        if (!tooltipModule || location === null) {
            removeElement(this.chart.element.id + '_tooltip');
            return;
        }
        if (isFirst) {
            this.svgTooltip = new SVGTooltip({
                opacity: chart.tooltip.opacity ? chart.tooltip.opacity : ((this.chart.theme === 'Material3' || this.chart.theme === 'Material3Dark' || this.chart.theme.indexOf('Bootstrap5') > -1) ? 1 : 0.75),
                header: this.headerText,
                content: this.text,
                fill: chart.tooltip.fill,
                border: chart.tooltip.border,
                enableAnimation: this.control.tooltip.followPointer
                    || this.control.tooltip.split ? false : chart.tooltip.enableAnimation,
                location: this.control.tooltip.followPointer
                    ? { x: chart.mouseX, y: chart.mouseY } : location,
                seriesTypes: seriesTypes,
                followPointer: this.control.tooltip.followPointer,
                splitLocations: splitLocations,
                splitClipBounds: splitClipBounds,
                split: this.control.getModuleName() === 'chart' ? this.control.tooltip.split : false,
                shared: this.control.tooltip.shared,
                crosshair: crosshairEnabled,
                shapes: shapes,
                markerImage: templatePoint && templatePoint.marker && templatePoint.marker.imageUrl !== '' ? templatePoint.marker.imageUrl : '',
                clipBounds: this.chart.chartAreaType === 'PolarRadar' ? new ChartLocation(0, 0) : clipLocation,
                areaBounds: bounds,
                palette: this.findPalette(),
                template: this.control.tooltip.split ? this.template :
                    (customTemplate || this.template),
                data: templatePoint,
                theme: chart.theme,
                offset: offset,
                textStyle: chart.tooltip.textStyle,
                isNegative: (series.isRectSeries && series.type !== 'Waterfall' && point && point.y < 0),
                inverted: this.chart.tooltip.split ? this.chart.requireInvertedAxis
                    : this.chart.requireInvertedAxis && series.isRectSeries,
                arrowPadding: (this.text.length > 1 || this.chart.stockChart || (this.chart.tooltip.location.x !== null
                    || this.chart.tooltip.location.y !== null) || this.control.tooltip.followPointer)
                    && !this.control.tooltip.split ? 0 : 7,
                availableSize: chart.availableSize,
                duration: this.chart.tooltip.duration,
                isCanvas: this.chart.enableCanvas,
                isFixed: (this.chart.tooltip.location.x !== null || this.chart.tooltip.location.y !== null),
                isTextWrap: chart.tooltip.enableTextWrap && chart.getModuleName() === 'chart',
                blazorTemplate: { name: 'Template', parent: this.chart.tooltip },
                controlInstance: this.chart,
                enableRTL: chart.enableRtl,
                controlName: 'Chart',
                allowHighlight: chart.getModuleName() === 'chart' && !series.marker.allowHighlight,
                tooltipRender: () => {
                    tooltipModule.removeHighlight();
                    tooltipModule.highlightPoints();
                    tooltipModule.updatePreviousPoint(extraPoints);
                },
                animationComplete: (args) => {
                    if (args.tooltip.fadeOuted) {
                        tooltipModule.fadeOut(tooltipModule.previousPoints);
                    }
                },
                showHeaderLine: this.chart.tooltip.showHeaderLine,
                showNearestTooltip: this.chart.tooltip.showNearestTooltip
            });
            this.svgTooltip.appendTo(this.getElement(this.element.id + '_tooltip'));
        }
        else {
            if (this.svgTooltip) {
                this.svgTooltip.location = this.control.tooltip.followPointer
                    ? { x: chart.mouseX, y: chart.mouseY } : location;
                this.svgTooltip.seriesTypes = seriesTypes;
                this.svgTooltip.followPointer = this.control.tooltip.followPointer;
                this.svgTooltip.splitClipBounds = splitClipBounds;
                this.svgTooltip.splitLocations = splitLocations;
                this.svgTooltip.content = this.text;
                this.svgTooltip.header = this.headerText;
                this.svgTooltip.offset = offset;
                this.svgTooltip.palette = this.findPalette();
                this.svgTooltip.shapes = shapes;
                this.svgTooltip.data = templatePoint;
                this.svgTooltip.template = this.template;
                this.svgTooltip.controlName = 'Chart';
                this.svgTooltip.crosshair = crosshairEnabled;
                this.svgTooltip.textStyle = chart.tooltip.textStyle;
                this.svgTooltip.isNegative = (series.isRectSeries && series.type !== 'Waterfall' && point && point.y < 0);
                this.svgTooltip.clipBounds = this.chart.chartAreaType === 'PolarRadar' ? new ChartLocation(0, 0) : clipLocation;
                this.svgTooltip.arrowPadding = (this.text.length > 1 || this.chart.stockChart || (this.chart.tooltip.location.x !== null
                    || this.chart.tooltip.location.y !== null) || this.control.tooltip.followPointer)
                    && !this.control.tooltip.split ? 0 : 7;
                this.svgTooltip.allowHighlight = chart.getModuleName() === 'chart' && !series.marker.allowHighlight;
                this.svgTooltip.dataBind();
            }
        }
        if (this.chart.isReact) {
            this.chart.renderReactTemplates();
        }
    }
    findPalette() {
        const colors = [];
        for (const data of this.currentPoints) {
            colors.push(this.findColor(data, data.series));
        }
        return colors;
    }
    findColor(data, series) {
        if (series.isRectSeries && (series.type === 'Candle' || series.type === 'Hilo' || series.type === 'HiloOpenClose')) {
            return data.point.color;
        }
        else {
            return (data.point.color && data.point.color !== '#ffffff' ? data.point.color
                : data.point.interior) ||
                series.marker.fill || series.interior;
        }
    }
    updatePreviousPoint(extraPoints) {
        if (extraPoints) {
            this.currentPoints = this.currentPoints.concat(extraPoints);
        }
        this.previousPoints = extend([], this.currentPoints, null, true);
    }
    fadeOut(data) {
        const svgElement = this.chart.enableCanvas ? this.getElement(this.element.id + '_tooltip_group') :
            this.getElement(this.element.id + '_tooltip_svg') || this.getElement(this.element.id + '_tooltipparent_template');
        const isTooltip = (svgElement && parseInt(svgElement.getAttribute('opacity'), 10) > 0);
        if (!isTooltip) {
            this.valueX = null;
            this.valueY = null;
            this.currentPoints = [];
            this.removeHighlight();
            this.removeHighlightedMarker(data, true);
            this.svgTooltip = null;
            this.control.trigger('animationComplete', {});
        }
    }
    /*
    * @hidden
    */
    removeHighlightedMarker(data, fadeOut) {
        if (this.chart.markerRender) {
            for (const item of data) {
                removeElement(this.element.id + '_Series_' + item.series.index +
                    '_Point_' + item.point.index + '_Trackball');
                this.chart.markerRender.removeHighlightedMarker(item.series, item.point, fadeOut);
            }
        }
        this.previousPoints = [];
    }
    // public triggerEvent(point: PointData | AccPointData, isFirst: boolean, textCollection: string, firstText: boolean = true): boolean {
    //     let argsData: ITooltipRenderEventArgs = {
    //         cancel: false, name: tooltipRender, text: textCollection,
    //         point: point.point, series: point.series, textStyle: this.textStyle
    //     };
    //     this.chart.trigger(tooltipRender, argsData);
    //     if (!argsData.cancel) {
    //         if (point.series.type === 'BoxAndWhisker') {
    //             this.removeText();
    //             isFirst = true;
    //         }
    //         this.formattedText = this.formattedText.concat(argsData.text);
    //         this.text = this.formattedText;
    //     }
    //     return !argsData.cancel;
    // }
    removeText() {
        this.textElements = [];
        const element = this.getElement(this.element.id + '_tooltip_group');
        if (element && element.childNodes.length > 0) {
            while (element.lastChild && element.childNodes.length !== 1) {
                element.removeChild(element.lastChild);
            }
        }
    }
    stopAnimation() {
        stopTimer(this.toolTipInterval);
    }
    /**
     * Removes the tooltip on mouse leave.
     *
     * @returns {void}
     * @private
     */
    removeTooltip(duration) {
        const tooltipElement = this.getElement(this.element.id + '_tooltip');
        // const tooltipTemplate: HTMLElement = tooltipElement ? this.getElement(tooltipElement.id + 'parent_template') : null;
        // const isTemplateRendered: boolean = tooltipTemplate && tooltipTemplate.innerText !== '<div></div>';
        this.stopAnimation();
        if (tooltipElement && this.previousPoints.length > 0) {
            this.toolTipInterval = +setTimeout(() => {
                if (this.svgTooltip) {
                    this.svgTooltip.fadeOut();
                }
            }, duration);
        }
    }
    /**
     * Gets the formatted tooltip value by applying the specified inline format when it matches the value type.
     *
     * @param {any} rawValue - The raw tooltip value to format.
     * @param {string} inlineFormat - The inline format string specified in the tooltip token.
     * @param {string} fallbackText - The fallback tooltip text returned when formatting cannot be applied.
     * @param {any} intl - The internationalization instance used to format date and number values.
     * @param {boolean} useGrouping - Specifies whether grouping separators should be used while formatting numbers.
     * @returns {string} - Returns the formatted tooltip value, or the fallback text when the format is not applicable.
     */
    getFormattedTooltipValue(rawValue, inlineFormat, fallbackText, intl, useGrouping) {
        // Date formatting
        if (rawValue instanceof Date && !this.isNumericFormat(inlineFormat) && intl) {
            return intl.getDateFormat({ format: inlineFormat })(rawValue);
        }
        // Number formatting
        if (typeof rawValue === 'number' && this.isNumericFormat(inlineFormat) && intl) {
            return intl.getNumberFormat({
                format: inlineFormat,
                useGrouping: useGrouping
            })(rawValue);
        }
        return fallbackText;
    }
    /**
     * Checks whether the specified inline format is a numeric format.
     *
     * @param {string} format - The inline format string to validate.
     * @returns {boolean} - Returns true when the format is numeric; otherwise, false.
     * @private
     */
    isNumericFormat(format) {
        format = format.trim();
        return /^[nNpPcCeE]\d*$/.test(format) || /[0#%]/.test(format);
    }
}
